import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Divider,
  Tooltip,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useTranslation } from 'react-i18next';

// IMPORT SERVICES
import { 
  downloadLogProtocolPDF, 
  getLogProtocolPdfBlob
} from '../../../services/logProtocolsService';

// IMPORT MODALS
import SimplePdfPreviewDialog from '../../PIIAnonymizerPage/PIIFileUploadPanel/SimplePdfPreviewDialog'; 
import PIIRelatedFilesDialog from './PIIRelatedFilesDialog';

const TYPE_LABELS = {
  PII_FILE_ANONYMIZATION: 'PII súbor',
};

const PIIProtocolItem = ({ protocol }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  
  // States for protocol download and preview
  const [downloading, setDownloading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBlob, setPreviewBlob] = useState(null);

  // States for Related Files Dialog
  const [relatedModalOpen, setRelatedModalOpen] = useState(false);

  // MUI Snackbar State
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const createdAt = protocol.created_at || protocol.createdAt;
  const createdDate = createdAt ? new Date(createdAt) : null;
  const typeText = TYPE_LABELS[protocol.type] || protocol.type;
  
  const relatedFiles = protocol.related_files || [];
  const hasRelatedFiles = relatedFiles.length > 0;

  const showNotification = (message, severity = 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // --- STANDARD PROTOCOL DOWNLOAD ---
  const handleDownload = async () => {
    if (!protocol?.id || downloading) return;
    try {
      setDownloading(true);
      await downloadLogProtocolPDF(protocol.id);
    } catch (err) {
      console.error('Failed to download PDF', err);
      showNotification(t('admin.pii.protocol.downloadError', 'Nepodarilo sa stiahnuť PDF protokolu.'));
    } finally {
      setDownloading(false);
    }
  };

  // --- PROTOCOL IN-MEMORY PREVIEW ---
  const handleOpenPreview = async () => {
    if (!protocol?.id || previewLoading) return;
    try {
      setPreviewLoading(true);
      const blob = await getLogProtocolPdfBlob(protocol.id); 
      if (!blob) throw new Error("No blob returned");
      setPreviewBlob(blob);
      setPreviewOpen(true);
    } catch (err) {
      console.error('Failed to load PDF preview', err);
      showNotification(t('admin.pii.protocol.previewError', 'Nepodarilo sa načítať náhľad protokolu.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewBlob(null);
  };

  return (
    <>
      <Box
        sx={(theme) => ({
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          p: 1.5,
          mb: 1.5,
          bgcolor: 'background.paper',
          transition: '0.2s',
          '&:hover': {
            boxShadow: 1,
            borderColor: 'divider',
          },
        })}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          {/* LEFT SIDE */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 200 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                flexShrink: 0,
              }}
            >
              <DescriptionIcon sx={{ fontSize: '1rem' }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Tooltip title={protocol.title || ''}>
                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                  {protocol.title || t('admin.pii.protocol.untitled', 'Protokol anonymizácie')}
                </Typography>
              </Tooltip>

              {createdDate && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {createdDate.toLocaleString('sk-SK')}
                </Typography>
              )}
            </Box>
          </Stack>

          {/* RIGHT SIDE ACTIONS */}
          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
            {protocol.type && (
              <Chip
                size="small"
                label={typeText}
                sx={{
                  fontSize: '0.65rem',
                  height: 22,
                  borderRadius: 1.5,
                  bgcolor: 'action.selected',
                  color: 'text.secondary',
                  display: { xs: 'none', sm: 'inline-flex' }
                }}
              />
            )}

            {/* RELATED FILES BUTTON */}
            {hasRelatedFiles && (
              <Tooltip title={t('admin.pii.protocol.viewRelatedFiles', 'Zobraziť súvisiace súbory')}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<FolderOpenIcon />}
                  onClick={() => setRelatedModalOpen(true)}
                  sx={{ height: 28, px: 1, fontSize: '0.75rem', textTransform: 'none', borderRadius: 2 }}
                >
                  Súbory ({relatedFiles.length})
                </Button>
              </Tooltip>
            )}

            {/* PREVIEW PROTOCOL EYE ICON */}
            <Tooltip title={t('admin.pii.protocol.preview', 'Náhľad protokolu')}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenPreview}
                  disabled={previewLoading || downloading || !protocol?.id}
                  sx={{ color: 'primary.main', p: 0.5 }}
                >
                  {previewLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            {/* NORMAL PROTOCOL DOWNLOAD BUTTON */}
            <Tooltip
              title={
                downloading
                  ? t('admin.pii.protocol.downloading', 'Sťahujem protokol…')
                  : t('admin.pii.protocol.downloadPdf', 'Stiahnuť PDF protokolu')
              }
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDownload}
                  disabled={downloading || previewLoading || !protocol?.id}
                  sx={{ height: 28, px: 1.5, fontSize: '0.75rem', borderRadius: 2, fontWeight: 600 }}
                >
                  {downloading
                    ? t('admin.pii.protocol.downloadingShort', 'Sťahujem…')
                    : t('admin.pii.protocol.download', 'Stiahnuť')}
                </Button>
              </span>
            </Tooltip>

            {/* Expand/collapse protocol text */}
            <IconButton size="small" onClick={() => setOpen((prev) => !prev)} sx={{ ml: 0.5 }}>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Stack>
        </Stack>

        {/* EXPANDED TEXT DETAIL */}
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 1.5 }} />
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              color: 'text.secondary',
              bgcolor: 'action.hover',
              p: 1.5,
              borderRadius: 2
            }}
          >
            {protocol.text || t('admin.pii.protocol.noText', 'Detail protokolu nie je k dispozícii.')}
          </Typography>
        </Collapse>
      </Box>

      {/* ---------------- MODALS ---------------- */}
      <SimplePdfPreviewDialog
        open={previewOpen}
        title={protocol.title || t('admin.pii.protocol.untitled', 'Protokol anonymizácie')}
        fileBlob={previewBlob}
        onClose={handleClosePreview}
        t={t}
      />

      <PIIRelatedFilesDialog
        open={relatedModalOpen}
        onClose={() => setRelatedModalOpen(false)}
        relatedFiles={relatedFiles}
        t={t}
        onError={showNotification}
      />

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PIIProtocolItem;